import {
  createDefaultLightweightAutomationInputSource,
  createDefaultLightweightAutomationIterationPolicy,
  formatSecretRefUri,
  getBuiltinLightweightAutomationModule,
  listBuiltinLightweightAutomationModules,
  parseSecretRefUri,
  type LightweightAutomationModuleKey,
  type LightweightAutomationRecord,
  type LightweightAutomationRunRecord,
  type LightweightAutomationSupportedInputSourceKind,
} from "../../../../../packages/shared-types/src/index.js";

export type { LightweightAutomationRecord, LightweightAutomationRunRecord };
export type AutomationRecord = LightweightAutomationRecord;
export type AutomationRunRecord = LightweightAutomationRunRecord;
export type AutomationOutputMode = LightweightAutomationRecord["delivery"]["mode"];
export type AutomationStatus = NonNullable<LightweightAutomationRecord["status"]>;
export type AutomationModuleKey = LightweightAutomationModuleKey;

type SecretRef = NonNullable<
  NonNullable<LightweightAutomationRecord["delivery"]["apiBatch"]>["authTokenSecretRef"]
>;

export const AUTOMATION_MODULE_OPTIONS = listBuiltinLightweightAutomationModules().map(
  (entry) => entry.key,
) as AutomationModuleKey[];

export const AUTOMATION_MODULE_METADATA: Record<
  AutomationModuleKey,
  {
    label: string;
    description: string;
    riskTier: string;
    supportedInputSources: string[];
  }
> = Object.fromEntries(
  listBuiltinLightweightAutomationModules().map((entry) => [
    entry.key,
    {
      label: entry.label,
      description: entry.description,
      riskTier: entry.riskTier,
      supportedInputSources: [...entry.supportedInputSources],
    },
  ]),
) as Record<
  AutomationModuleKey,
  {
    label: string;
    description: string;
    riskTier: string;
    supportedInputSources: string[];
  }
>;

export function resolveInternalAutomationModuleKey(params: {
  preferredModuleKey?: string;
  inputSourceKind: LightweightAutomationSupportedInputSourceKind;
}): LightweightAutomationModuleKey {
  const preferred = params.preferredModuleKey?.trim();
  if (preferred) {
    const preferredModule = getBuiltinLightweightAutomationModule(preferred);
    if (preferredModule?.supportedInputSources.includes(params.inputSourceKind)) {
      return preferredModule.key;
    }
  }
  return (
    listBuiltinLightweightAutomationModules().find((entry) =>
      entry.supportedInputSources.includes(params.inputSourceKind),
    )?.key ??
    preferred ??
    "research"
  );
}

export function createDefaultAutomationRecord(
  tenantId: string,
  automationId = `automation_${Date.now()}`,
): LightweightAutomationRecord {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const nowIso = new Date().toISOString();
  return {
    tenantId,
    automationId,
    name: "New Automation",
    enabled: true,
    agentId: "",
    ownerMode: "self",
    ownerUserId: "",
    moduleKey: resolveInternalAutomationModuleKey({ inputSourceKind: "none" }),
    instructionsMarkdown: "",
    inputSource: createDefaultLightweightAutomationInputSource(),
    iterationPolicy: createDefaultLightweightAutomationIterationPolicy(),
    status: "draft",
    execution: {
      mode: "manual",
      executionClass: "operator_maintenance",
      schedule: {
        kind: "cron",
        cron: "0 8 * * *",
        tz: timezone,
      },
    },
    delivery: {
      mode: "message",
      message: {
        destination: "inbox",
      },
    },
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}

export function parseSecretRefUriInput(value: string): SecretRef | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  return parseSecretRefUri(trimmed) ?? undefined;
}

export function formatSecretRefUriInput(value?: SecretRef): string {
  return value ? formatSecretRefUri(value) : "";
}
